/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React, { useEffect, useRef, useState } from 'react';

import { COVERAGE_STEP } from './coverage-filter.types';

/**
 * Narrowed resource interface for the CoverageFilter component.
 * Consumers pass only the keys this component needs.
 */
export interface ICoverageFilterResources {
    coveragePrompt: string;
    coverageDescription: string;
    coverageTargetLabel: string;
    coverageGateUnlockHint: string;
    coverageGateText: string;
    coverageStepDecreaseAria: string;
    coverageStepIncreaseAria: string;
    coverageInputAria: string;
}

/**
 * Props for the CoverageFilter component.
 */
export interface ICoverageFilterProps {
    target: number;
    onChange: (next: number) => void;
    resources: ICoverageFilterResources;
    locked: boolean;
    onLockedAttempt?: () => void;
}

const LockGlyph: React.FC = () => (
    <svg width='11' height='11' viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth='1.7' aria-hidden='true'>
        <rect x='3.5' y='7' width='9' height='6.5' rx='1.3' />
        <path d='M5.6 7V5.2a2.4 2.4 0 0 1 4.8 0V7' />
    </svg>
);

/**
 * Coverage filter stepper UI rendered inside the refiner dropdown.
 * Allows the shopper to enter a target area (pi²) for availability filtering.
 * @param props - The component props.
 * @returns The coverage filter JSX element.
 */
export const CoverageFilter: React.FC<ICoverageFilterProps> = ({ target, onChange, resources, locked, onLockedAttempt }) => {
    const [shake, setShake] = useState(false);
    const timerRef = useRef<number | undefined>(undefined);
    const gateNoteId = 'ms-coverage-filter-gate-note';

    useEffect(() => {
        return () => {
            window.clearTimeout(timerRef.current);
        };
    }, []);

    useEffect(() => {
        if (!locked) {
            setShake(false);
            window.clearTimeout(timerRef.current);
        }
    }, [locked]);

    const blocked = () => {
        setShake(true);
        window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => setShake(false), 460);
        onLockedAttempt?.();
    };

    const guard = (fn: () => void) => () => {
        if (locked) {
            blocked();
            return;
        }

        fn();
    };

    return (
        <div
            className={`ms-coverage-filter${locked ? ' ms-coverage-filter--locked' : ''}`}
            role='group'
            aria-label={resources.coveragePrompt}
            aria-disabled={locked || undefined}
        >
            <div className='ms-coverage-filter__left'>
                <div className='ms-coverage-filter__prompt'>{resources.coveragePrompt}</div>
                <div className='ms-coverage-filter__description'>{resources.coverageDescription}</div>
            </div>

            {locked && (
                <div className='ms-coverage-filter__gate-note' id={gateNoteId} role='note'>
                    <div className='ms-coverage-filter__gate-text'>{resources.coverageGateText}</div>
                </div>
            )}

            <div className='ms-coverage-filter__right'>
                <div className='ms-coverage-filter__note' aria-live='polite'>
                    {locked && <LockGlyph />}
                    <div className='ms-coverage-filter__target-label'>{resources.coverageTargetLabel}</div>
                </div>

                <div className={`ms-coverage-filter__target${shake ? ' is-shake' : ''}`} onClick={locked ? blocked : undefined}>
                    <button
                        type='button'
                        className={`ms-coverage-filter__step${locked ? ' is-disabled' : ''}`}
                        aria-label={resources.coverageStepDecreaseAria}
                        aria-disabled={locked}
                        aria-describedby={locked ? gateNoteId : undefined}
                        tabIndex={locked ? -1 : 0}
                        onClick={guard(() => onChange(Math.max(0, target - COVERAGE_STEP)))}
                    >
                        &#8722;
                    </button>
                    <input
                        className={`ms-coverage-filter__value${locked ? ' is-disabled' : ''}`}
                        inputMode='numeric'
                        aria-label={resources.coverageInputAria}
                        aria-disabled={locked}
                        aria-describedby={locked ? gateNoteId : undefined}
                        readOnly={locked}
                        value={target}
                        onChange={e => {
                            if (locked) {
                                blocked();
                                return;
                            }
                            onChange(Math.max(0, parseInt(e.target.value || '0', 10) || 0));
                        }}
                    />
                    <button
                        type='button'
                        className={`ms-coverage-filter__step${locked ? ' is-disabled' : ''}`}
                        aria-label={resources.coverageStepIncreaseAria}
                        aria-disabled={locked}
                        aria-describedby={locked ? gateNoteId : undefined}
                        tabIndex={locked ? -1 : 0}
                        onClick={guard(() => onChange(target + COVERAGE_STEP))}
                    >
                        +
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CoverageFilter;
